import { Box, Typography } from "@mui/material";
import type { JSX } from "react";

export interface PaginationMeta {
  page: number;
  perPage: number;
  totalPages: number;
  totalCount: number;
}

/**
 * Prev/next paging with a count.
 *
 * Renders NOTHING while `pagination` is undefined (first load) or when
 * there is only one page — a lone disabled "Page 1 of 1" under an empty
 * table is noise that makes the empty state read as a failure.
 */
export function Pager({
  pagination,
  onPage,
}: {
  pagination: PaginationMeta | undefined;
  onPage: (page: number) => void;
}): JSX.Element | null {
  if (!pagination || pagination.totalPages <= 1) return null;

  const { page, totalPages, totalCount } = pagination;

  return (
    <Box
      sx={{
        mt: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
        Page {page} of {totalPages} · {totalCount.toLocaleString()} total
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        <PageButton disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </PageButton>
        <PageButton disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
          Next
        </PageButton>
      </Box>
    </Box>
  );
}

function PageButton({
  disabled,
  onClick,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  children: string;
}): JSX.Element {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        height: 32,
        px: 1.75,
        borderRadius: 1.25,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        font: "inherit",
        fontSize: 12.5,
        fontWeight: 500,
        cursor: "pointer",
        color: "text.primary",
        "&:hover:not(:disabled)": { borderColor: "text.disabled" },
        "&:disabled": { opacity: 0.45, cursor: "not-allowed" },
      }}
    >
      {children}
    </Box>
  );
}
